import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PAT } from "@/constants/testIds";
import { formatDate } from "@/lib/patientChart";
import { getPatientSmsConsent, recordPatientSmsConsent } from "@/lib/smsConsentApi";

function statusLabel(status) {
  if (status === "opted_in") return "Opted in";
  if (status === "opted_out") return "Opted out";
  return "Not opted in";
}

function methodLabel(method) {
  if (method === "staff_assisted") return "Staff-assisted";
  if (method === "carrier_inbound") return "Carrier inbound";
  return method || "—";
}

function stamp(iso) {
  if (!iso) return "—";
  const formatted = formatDate(iso, "MMM d, yyyy h:mm a");
  return formatted && formatted !== "—" ? formatted : iso;
}

export default function PatientSmsConsentCard({ patientId, accessReadOnly = false }) {
  const [consent, setConsent] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    if (!patientId) {
      setConsent(null);
      setAuthorized(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setAuthorized(false);
    getPatientSmsConsent(patientId)
      .then((data) => {
        if (!cancelled) setConsent(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Unable to load SMS consent.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const save = async (action) => {
    if (!patientId || saving || accessReadOnly) return;
    if (action === "grant" && authorized !== true) return;
    setSaving(true);
    setError("");
    try {
      const data = await recordPatientSmsConsent(patientId, {
        action,
        patientAuthorized: action === "grant" ? authorized : false,
      });
      setConsent(data);
      setAuthorized(false);
      toast.success(action === "grant" ? "SMS consent recorded." : "SMS opt-out recorded.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text = typeof detail === "string" ? detail : "Unable to update SMS consent.";
      setError(text);
      toast.error(text);
    } finally {
      setSaving(false);
    }
  };

  const optedIn = consent?.status === "opted_in";
  const when = optedIn ? consent?.consentedAt : consent?.optedOutAt;

  return (
    <section data-testid={PAT.smsConsentCard} className="chart-panel p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">Appointment SMS consent</h2>
        <span
          data-testid={PAT.smsConsentStatus}
          className={`rounded-md border px-2 py-0.5 text-xs font-semibold ${
            optedIn
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-slate-200 bg-slate-50 text-slate-700"
          }`}
        >
          {loading ? "Loading…" : statusLabel(consent?.status)}
        </span>
      </div>

      <p className="mb-3 text-xs text-slate-500">
        Staff-assisted opt-in only. The patient (or authorized representative) must
        review the disclosure and authorize SMS. This checkbox is never pre-checked.
        Automatic appointment reminders stay off.
      </p>

      <dl className="mb-3 grid grid-cols-2 gap-3">
        <div>
          <dt className="text-metadata font-semibold uppercase tracking-wide text-slate-500">Method</dt>
          <dd data-testid={PAT.smsConsentMethod} className="mt-0.5 text-sm text-slate-900">
            {consent?.method ? methodLabel(consent.method) : "Not recorded"}
          </dd>
        </div>
        <div>
          <dt className="text-metadata font-semibold uppercase tracking-wide text-slate-500">
            {optedIn ? "Consented" : consent?.status === "opted_out" ? "Opted out" : "Last change"}
          </dt>
          <dd data-testid={PAT.smsConsentWhen} className="mt-0.5 text-sm text-slate-900">
            {when ? stamp(when) : "—"}
          </dd>
        </div>
      </dl>

      <pre
        data-testid={PAT.smsConsentDisclosure}
        className="mb-3 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-800"
      >
        {consent?.disclosureText || "Loading disclosure…"}
      </pre>

      {consent?.carrierInboundLive === false ? (
        <p data-testid={PAT.smsConsentNote} className="mb-3 text-xs text-slate-500">
          {consent.carrierInboundNote ||
            "Carrier STOP/HELP inbound handling is not live. Recording opt-out here updates EMRbyMD application consent only."}
        </p>
      ) : null}

      {error ? (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      ) : null}

      {accessReadOnly ? null : !optedIn ? (
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              data-testid={PAT.smsConsentAuthorize}
              className="mt-1 h-4 w-4 accent-[#0d5c4d]"
              checked={authorized}
              onChange={(event) => setAuthorized(event.target.checked)}
            />
            <span>{consent?.staffAttestationText}</span>
          </label>
          <Button
            type="button"
            data-testid={PAT.smsConsentGrant}
            className="bg-[#0d5c4d] hover:bg-[#0a4b3f]"
            disabled={saving || loading || authorized !== true}
            onClick={() => save("grant")}
          >
            {saving ? "Saving…" : "Record SMS consent"}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          data-testid={PAT.smsConsentRevoke}
          disabled={saving || loading}
          onClick={() => save("revoke")}
        >
          {saving ? "Saving…" : "Record SMS opt-out"}
        </Button>
      )}
    </section>
  );
}
